import { useState } from 'react';
import { X, Search, ChevronRight, Activity, Heart } from 'lucide-react';

interface Device {
  name: string;
  icon: string;
  category: 'diabetes' | 'fitness' | 'cgm' | 'insulin';
  connected?: boolean;
}

interface AddDeviceModalProps {
  onClose: () => void;
}

export function AddDeviceModal({ onClose }: AddDeviceModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const diabetesAccounts: Device[] = [
    { name: 'GlucoRX', icon: '🩺', category: 'diabetes' },
  ];

  const healthApps: Device[] = [
    { name: 'Apple Health', icon: '🍎', category: 'fitness' },
    { name: 'Fitbit', icon: '⌚', category: 'fitness' },
    { name: 'Withings', icon: '📱', category: 'fitness' },
    { name: 'Garmin Connect', icon: '🏃', category: 'fitness' },
    { name: 'Strava', icon: '🚴', category: 'fitness' },
  ];

  const cgmDevices: Device[] = [
    { name: 'FreeStyle Libre', icon: '🔵', category: 'cgm' },
    { name: 'Dexcom G6', icon: '🟢', category: 'cgm' },
    { name: 'Medtronic Guardian', icon: '🟡', category: 'cgm' },
    { name: 'Eversense', icon: '🟣', category: 'cgm' },
  ];

  const insulinDevices: Device[] = [
    { name: 'Omnipod DASH', icon: '💊', category: 'insulin' },
    { name: 'Medtronic MiniMed', icon: '⚕️', category: 'insulin' },
    { name: 'Tandem t:slim', icon: '🏥', category: 'insulin' },
    { name: 'InPen', icon: '✏️', category: 'insulin' },
  ];

  const filterDevices = (devices: Device[]) => {
    if (!searchQuery) return devices;
    return devices.filter((device) =>
      device.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const handleConnect = (device: Device) => {
    // Device integration in development
    alert(`Connecting to ${device.name}... (Feature coming soon)`);
  };

  const DeviceList = ({
    devices,
    title,
  }: {
    devices: Device[];
    title: string;
  }) => {
    const filteredDevices = filterDevices(devices);
    if (filteredDevices.length === 0) return null;

    return (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <button
            onClick={() => setExpandedSection(expandedSection === title ? null : title)}
            className="text-dropsugar-green text-sm font-medium hover:text-dropsugar-green-dark"
          >
            {expandedSection === title ? 'Hide' : 'View all'}
          </button>
        </div>
        <div className="space-y-2">
          {filteredDevices
            .slice(0, expandedSection === title ? undefined : 3)
            .map((device) => (
              <button
                key={device.name}
                onClick={() => handleConnect(device)}
                className="w-full flex items-center justify-between p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-dropsugar-green hover:bg-dropsugar-green/10 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="text-2xl">{device.icon}</div>
                  <span className="font-medium text-gray-900">{device.name}</span>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-dropsugar-green group-hover:translate-x-1 transition-all" />
              </button>
            ))}
        </div>
      </div>
    );
  };

  const CategoryCard = ({
    icon: Icon,
    title,
    description,
  }: {
    icon: React.ElementType;
    title: string;
    description: string;
  }) => (
    <button
      type="button"
      className="flex items-start gap-4 p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-dropsugar-green hover:bg-dropsugar-green/10 transition-all group text-left"
    >
      <div className="p-3 bg-dropsugar-green/10 group-hover:bg-dropsugar-green/20 rounded-lg transition-colors">
        <Icon className="w-6 h-6 text-dropsugar-green" />
      </div>
      <div className="flex-1">
        <h4 className="font-semibold text-gray-900 mb-1">{title}</h4>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-dropsugar-green group-hover:translate-x-1 transition-all flex-shrink-0 mt-2" />
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-50 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="bg-white border-b border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Add device</h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search for a device name or brand"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-dropsugar-green focus:ring-2 focus:ring-blue-200 outline-none transition-all"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <DeviceList devices={diabetesAccounts} title="Connect an account" />
          <DeviceList devices={healthApps} title="Health & fitness apps" />

          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Connect a device</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <CategoryCard
                icon={Activity}
                title="Glucose CGMs & BG meters"
                description="Continuous glucose monitors and blood glucose meters"
              />
              <CategoryCard
                icon={Heart}
                title="Insulin Pens & pumps"
                description="Smart insulin pens and pump systems"
              />
            </div>
          </div>

          {searchQuery && (
            <>
              <DeviceList devices={cgmDevices} title="CGM Devices" />
              <DeviceList devices={insulinDevices} title="Insulin Devices" />
            </>
          )}

          <div className="mt-6 p-4 bg-dropsugar-green/10 rounded-lg border-2 border-dropsugar-green/30">
            <p className="text-sm text-blue-900">
              <strong>Note:</strong> Device integration features are currently in development.
              You&apos;ll be able to sync data from your diabetes devices and health apps soon.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
